import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
    NetworkStatusDialogComponent,
    NetworkStatusDialogData,
} from 'app/shared/components/atoms/network-status-dialog/network-status-dialog.component';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

/**
 * Tracks whether the network status dialog is currently open.
 * Module-level state prevents multiple dialogs from stacking.
 */
let activeDialogRef: MatDialogRef<NetworkStatusDialogComponent> | null = null;

/**
 * Functional interceptor that detects network failures for all API calls.
 *
 * - If the user is already offline before the request, it blocks the request
 *   entirely (no HTTP call is made) and shows the offline dialog.
 * - If the request fails mid-flight with status 0, it shows either the offline
 *   or service-unavailable dialog depending on navigator.onLine.
 *
 * In both cases EMPTY is returned so the observable completes silently —
 * no error propagates to components, preventing the "Failed to fetch" toast.
 */
export const networkInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const dialog = inject(MatDialog);

    // Block the request immediately if already offline — no need to call the API
    if (!navigator.onLine) {
        showNetworkStatusDialog(dialog, 'offline');
        return EMPTY as Observable<HttpEvent<unknown>>;
    }

    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            // Request failed mid-flight
            if (error.status === 0) {
                if (!navigator.onLine) {
                    // Device is genuinely offline
                    showNetworkStatusDialog(dialog, 'offline');
                } else {
                    // Device is online but server is unreachable (server down,
                    // DNS failure, CORS, firewall drop, etc.)
                    showNetworkStatusDialog(dialog, 'service-unavailable');
                }
                // Return EMPTY so no error reaches component handlers —
                // this prevents the "No internet connection" / "Failed to fetch" toast.
                return EMPTY as Observable<HttpEvent<unknown>>;
            }

            return throwError(() => error);
        })
    );
};

/**
 * Opens the network status dialog if not already open.
 * Automatically closes when the browser's online event fires.
 */
function showNetworkStatusDialog(
    dialog: MatDialog,
    status: 'offline' | 'low-bandwidth' | 'service-unavailable'
): void {
    // Prevent multiple dialogs from stacking
    if (activeDialogRef) {
        return;
    }

    const dialogData: NetworkStatusDialogData = { status };

    activeDialogRef = dialog.open(NetworkStatusDialogComponent, {
        data: dialogData,
        disableClose: status === 'offline',
        autoFocus: false,
        restoreFocus: false,
        width: '400px',
        maxWidth: '90vw',
        backdropClass: 'network-status-dialog-backdrop',
    });

    activeDialogRef.afterClosed().subscribe(() => {
        activeDialogRef = null;
    });

    // Auto-close when connection is restored
    const onlineHandler = (): void => {
        if (activeDialogRef) {
            activeDialogRef.close();
            activeDialogRef = null;
        }
        globalThis.removeEventListener('online', onlineHandler);
    };
    globalThis.addEventListener('online', onlineHandler);
}
